// Kayak Content
import TwentyFourHourIcon from 'assets/images/psp/twentyfour-hour.svg';
import Six from 'assets/images/psp/six.svg';
import PaperWork from 'assets/images/psp/paperwork.svg';
import KayakHero from 'assets/images/psp/hero/kayak-hero.webp';
import Featured1 from 'assets/images/psp/FeaturedImages/kayakFeatured1.webp';
import Featured2 from 'assets/images/psp/FeaturedImages/kayakFeatured2.webp';
import Featured3 from 'assets/images/psp/FeaturedImages/kayakFeatured3.webp';

const kayakStorageCost =
	'### How to Prepare Your Kayak for Storage \n' +
	'#### Factors Affecting Cost\n' +
	'- **Location:** Urban areas typically cost more than rural locations\n' +
	'- **Climate Control:** Adds $20-$40 monthly but protects against extreme temperatures\n' +
	'- **Security Features:** Gated access and surveillance may increase costs slightly\n' +
	'- **Seasonal Demand:** Prices may fluctuate based on local paddling seasons\n\n' +
	'#### Money-Saving Tips\n' +
	'- Book during off-season months for better rates\n' +
	'- Consider sharing a larger unit with fellow paddlers\n' +
	'- Look for first-month-free promotions\n' +
	'- Choose standard units over climate-controlled if your area has mild weather';

const kayakStorageTips =
	'### Essential Pre-Storage Steps\n' +
	'**1. Clean Thoroughly**\n' +
	'- Rinse with fresh water to remove salt, sand, and debris\n' +
	'- Use mild soap for stubborn stains\n' +
	'- Dry completely to prevent mold and mildew\n' +
	"- Don't forget to clean inside storage compartments\n\n" +
	'**2. Inspect for Damage**\n' +
	'- Check for cracks, scratches, or worn areas\n' +
	'- Repair minor issues before storage\n' +
	'- Document any existing damage with photos\n' +
	'- Remove any gear that might attract pests\n\n' +
	'**3. Proper Positioning**\n\n' +
	'How to store a kayak in a storage unit depends on your space and kayak type:\n' +
	'- **Horizontal Storage:** Rest on padded sawhorses or foam blocks\n' +
	'- **Vertical Storage:** Use wall-mounted racks or lean against padded walls\n' +
	'- **Avoid:** Storing upside down or hanging by grab handles\n\n' +
	'### Storage Unit Setup Tips\n' +
	'**Organization Ideas:**\n' +
	'- Install hooks for paddles and life jackets\n' +
	'- Use clear bins for small accessories\n' +
	'- Create an inventory list for easy reference\n' +
	'- Leave some walking space around your kayak\n\n' +
	'**Protection Strategies:**\n' +
	'- Use furniture pads or old blankets as covers\n' +
	'- Place moisture absorbers in humid climates\n' +
	'- Slightly crack storage hatches for air circulation\n' +
	'- Position away from unit doors to prevent accidental damage';

const reviews = [
	{
		reviewTitle: 'SpareFoot Makes Moving a Piece of Cake!',
		link: 'https://www.trustpilot.com/reviews/53f4a8dc0000640002a00dbf',
		reviewRating: 5,
		reviewText:
			'I needed to change storage units because I have accumulated a lot of outdoor equipment...kayak, tents, backpacks, etc. I did a Google search, but it was taking me a while to sift through independent storage companies. I clicked on SpareFoot, called, told them what I needed and my budget, and they had me on to phone and to my storage facility in less than 5 minutes! Fantastic!',
		reviewerName: 'Jennifer Boley',
		reviewerInitials: 'JB',

		reviewDate: '',
	},
	{
		reviewTitle: 'Easy to Use',
		link: 'https://www.trustpilot.com/reviews/6873aee374abbc40cb6c61b2',
		reviewRating: 5,
		reviewText:
			'I just typed in what I was looking for, and several storage places popped up on SpareFoot. The best part is I can make a reservation on this site without a credit card. That made the experience doable.',
		reviewerName: 'Colorado Customer',
		reviewerInitials: 'CC',

		reviewDate: '',
	},
	{
		reviewTitle: 'Five-Star Review of SpareFoot',
		link: 'https://www.trustpilot.com/reviews/686e900ceea6c00d5e8a3c15',
		reviewRating: 5,
		reviewText:
			'Everyone I spoke to was personable and informative and walked me through the whole process. It was very easy to book online, and I received all of the information in a timely fashion. Good prices and I highly recommend!',
		reviewerName: 'MJ PRICE',
		reviewerInitials: 'MP',

		reviewDate: '',
	},
	{
		reviewTitle: 'SpareFoot Was Easy to Navigate',
		link: 'https://www.trustpilot.com/reviews/6862fe2ccf3a6c04abe409b5',
		reviewRating: 5,
		reviewText:
			'Site was easy to navigate and had many options. Locations, sizes of units, prices, and discounts.',
		reviewerName: 'Rafael Villa',
		reviewerInitials: 'RV',

		reviewDate: '',
	},
];

const kayakFaqs = [
	{
		question: 'What size storage unit do I need for my kayak?',
		answer:
			"The size of the storage unit you'll need depends primarily on your kayak's length and how much additional gear you plan to store. Most recreational kayaks (8–12 feet) fit comfortably in a 5x10 unit, while longer touring or sea kayaks (12–18 feet) work better in 5x15 or 5x20 units.\n\n" +
			"When determining what size storage unit to rent for kayaks, consider your kayak's length, whether you'll store it horizontally or vertically, and space needed for accessories like paddles, life jackets, and safety gear. A good rule of thumb is to choose a unit that's at least 2 feet longer than your kayak for horizontal storage.",
	},
	{
		question: 'Can I store my kayak vertically in a storage unit?',
		answer:
			'Yes, vertical kayak storage is an excellent option for maximizing space in your storage unit. Most standard storage units have 8-foot ceilings, which accommodate kayaks up to about 7.5 feet when stored vertically. This method works particularly well for shorter whitewater and recreational kayaks.\n\n' +
			"For kayak vertical storage, you'll need proper support to prevent damage to your boat's hull. Wall-mounted racks, foam padding, or even a simple lean-against-the-wall setup with protective padding can work well. The key is ensuring the kayak's weight is distributed evenly.\n\n" +
			"Vertical storage offers several advantages: it frees up floor space for other gear, makes it easier to store multiple kayaks in the same unit, and can be gentler on your kayak's hull compared to long-term horizontal storage. Make sure your kayak is completely dry before vertical storage to prevent water from pooling in compartments.",
	},
	{
		question: 'Do I need climate-controlled storage for my kayak?',
		answer:
			"[Climate-controlled storage](https://www.sparefoot.com/blog/do-you-need-climate-control-in-your-storage-unit) isn't absolutely necessary for most kayaks, but it can significantly extend your boat's lifespan, especially in extreme climates. Standard storage units work fine in moderate climates, but climate control becomes valuable in areas with high humidity, extreme temperatures, or dramatic seasonal changes.\n\n" +
			'Kayaks made from different materials have varying climate needs. Plastic kayaks are generally more resilient to temperature fluctuations, while composite kayaks (fiberglass, carbon fiber, or Kevlar) benefit more from stable temperatures and humidity levels. Wooden kayaks definitely need climate-controlled storage to prevent warping, cracking, and other moisture-related damage.\n\n' +
			'Consider climate control if you live in an area with hot summers (temperatures regularly over 90°F), very cold winters (below 20°F), or high humidity year-round. The additional cost of climate control ($20–$40 monthly) is often worth it to prevent costly repairs or premature replacement.',
	},
	{
		question: 'How do I protect my kayak from damage while in storage?',
		answer:
			'Protecting your kayak in storage starts with proper preparation and positioning. First, clean your kayak thoroughly and allow it to dry completely before storage. Any remaining moisture can lead to mold, mildew, or even structural damage over time.\n\n' +
			"For physical protection, avoid storing your kayak directly on concrete floors or against rough walls. Use padded sawhorses, foam blocks, or furniture pads to create barriers between your kayak and hard surfaces. If you're using vertical storage, ensure adequate padding where the kayak contacts the wall or storage rack.\n\n" +
			"Position your kayak away from the storage unit entrance to prevent accidental impacts when accessing other items. Create a clear path through your storage space and maintain adequate lighting for safe navigation. Consider covering your kayak with a breathable tarp or furniture pad to protect against dust and minor impacts, but ensure air circulation isn't completely blocked.",
	},
	{
		question: 'What accessories should I store with my kayak?',
		answer:
			'Storing your essential kayaking accessories together creates a convenient, organized system that makes pre-trip preparation much easier. Essential items to store with your kayak include paddles, personal flotation devices (PFDs), spray skirts, dry bags, and safety equipment like whistles and bilge pumps.\n\n' +
			'Organize accessories using clear storage bins, wall-mounted hooks, or shelving systems within your storage unit. This approach works particularly well for canoe and kayak storage, as you can dedicate specific areas for different types of gear. Keep frequently used items easily accessible near the unit entrance, while seasonal or backup gear can be stored in less accessible areas.\n\n' +
			'Consider the storage requirements of each accessory type. PFDs should be stored flat or hung to maintain their shape and effectiveness. Paddles can be hung on hooks or stored in vertical racks to save space. Electronic equipment like fish finders or GPS units should be removed and stored at home, as temperature fluctuations can damage sensitive electronics.',
	},
	{
		question: 'Can I access my kayak storage unit anytime?',
		answer:
			'Most storage facilities offer extended access hours, typically from 6 AM to 10 PM, though this varies by location and facility type. Some facilities provide 24/7 access, which is particularly valuable for kayakers who prefer early morning or evening paddling sessions.\n\n' +
			'When choosing a storage facility, consider your typical paddling schedule and whether you need weekend or holiday access. Many facilities maintain regular access hours even on weekends, recognizing that recreational activities often happen outside standard business hours. However, some smaller facilities might have restricted weekend hours, so verify this during your search.\n\n' +
			"Access methods vary between facilities. Some use traditional locks and keys, while others employ electronic gate systems, keypads, or smartphone apps for entry. Electronic systems often provide access logs, which can be helpful for security and personal record-keeping. Regardless of the system, you'll typically receive your access credentials immediately upon rental, allowing you to start using your storage unit right away.",
	},
];

export const KayakContent = {
	type: 'Kayak',
	sections: [
		{
			title: 'Kayak Storage Hero',
			background: 'yellow',
			modules: [
				{
					type: 'hero',
					background: 'lighterYellow',
					props: {
						content: {
							title: 'Find the Best Kayak Storage For You',
							description:
								"Tired of your kayak taking over your garage or backyard? Whether you're dealing with seasonal storage needs or just need more space at home, finding the right storage for kayaks doesn't have to be a headache. \n\n SpareFoot connects you with secure, affordable storage units that'll keep your paddle sports gear protected year-round.",
							image: KayakHero,
							CTAText: 'Search Now',
						},
					},
				},
			],
		},
		{
			title: 'nearby storage',
			noPaddingBottom: true,
			background: 'gray',
			modules: [
				{
					type: 'facilitiesNearYou',
					props: {
						title: 'Find Kayak Storage Near You',
					},
				},
			],
		},
		{
			title: 'Why Storage Units Are Perfect for Your Kayak',
			modules: [
				{
					type: 'content',
					props: {
						title: 'Why Storage Units Are Perfect for Your Kayak',
						contentType: 'column-type',
						showImageWidth: true,
						ShowBackgroundImage: false,
						content: [
							'![Kayak Storage](' +
								Six +
								')' +
								'\n\n' +
								'### Protection from the Elements \n\n' +
								'Your kayak is an investment worth protecting. Storage units specifically [climate-controlled units](https://www.sparefoot.com/climate-controlled-storage.html) shield your gear from:\n\n' +
								'- UV damage that can fade and weaken materials\n' +
								'- Temperature fluctuations that cause warping\n' +
								'- Rain, snow, and humidity that lead to mold and mildew\n' +
								'- Theft and vandalism\n\n',
							'![Kayak Storage](' +
								TwentyFourHourIcon +
								')' +
								'\n\n' +
								'### Year-Round Convenience\n\n' +
								'Not everyone lives where paddling season lasts 12 months. A kayak storage unit gives you:\n\n' +
								"- Easy access when you're ready to hit the water\n" +
								'- Climate-controlled options for premium protection\n' +
								'- Security features like gated access and surveillance\n' +
								'- More space at home for daily life\n\n',
							'![Kayak Storage](' +
								PaperWork +
								')' +
								'\n\n' +
								'### Flexible Solutions\n\n' +
								'Whether you need canoe and kayak storage for just your boat or want to store all your paddle sports gear together, storage units offer:\n\n' +
								'- Various size options to fit any budget\n' +
								'- Short-term and long-term rental flexibility\n' +
								'- Additional space for paddles, life jackets, and accessories\n' +
								'- No long-term commitments',
						],
					},
				},
			],
		},
		{
			title: 'Storage Unit Size Guide for Kayaks',
			noPaddingTop: true,
			modules: [
				{
					type: 'sizeChart',
					props: {
						title: 'Storage Unit Size Guide for Kayaks',
						headers: [
							{
								label: 'Kayak Type',
								key: 'Kayak Type',
							},
							{
								label: 'Typical Length',
								key: 'Typical Length',
							},
							{
								label: 'Recommended Unit Size',
								key: 'Recommended Unit Size',
							},
							{
								label: 'Can Store Additional Items?',
								key: 'Can Store Additional Items?',
							},
						],
						rows: [
							{
								'Kayak Type': 'Recreational',
								'Typical Length': '8-12 feet',
								'Recommended Unit Size': '5x10 or 5x15',
								'Can Store Additional Items?':
									'Yes, accessories like paddles, life jackets',
							},
							{
								'Kayak Type': 'Touring/Sea',
								'Typical Length': '12-18 feet',
								'Recommended Unit Size': '5x15 or 5x20',
								'Can Store Additional Items?':
									'Yes, including extra camping gear',
							},
							{
								'Kayak Type': 'Whitewater',
								'Typical Length': '6-9 feet',
								'Recommended Unit Size': '5x5 or 5x10',
								'Can Store Additional Items?':
									'Yes, there’s room for helmets and dry bags',
							},
							{
								'Kayak Type': 'Fishing',
								'Typical Length': '10-14 feet',
								'Recommended Unit Size': '5x10 or 5x15',
								'Can Store Additional Items?':
									'Yes, you can also store tackle, rod holders, and extra gear',
							},
							{
								'Kayak Type': 'Tandem',
								'Typical Length': '12-18 feet',
								'Recommended Unit Size': '5x15 or 5x20',
								'Can Store Additional Items?':
									'Yes, two sets of gear for each rider',
							},
						],
					},
				},
			],
		},
		{
			title: 'Storage Ideas for Kayaks in Different Unit Sizes',
			noPadding: true,
			modules: [
				{
					type: 'content',
					props: {
						title: 'Storage Ideas for Kayaks in Different Unit Sizes',
						ShowBackgroundImage: false,
						contentType: 'row-type',
						content: [
							"**[5x5 Storage Units](https://www.sparefoot.com/blog/5x5-storage-unit-size-guide):** Perfect for shorter whitewater kayaks or kids' boats. Consider vertical kayak storage to maximize space.\n\n" +
								'**[5x10 Storage Units](https://www.sparefoot.com/blog/5x10-storage-unit-size-guide):** The sweet spot for most recreational kayaks. Plenty of room for kayak vertical storage plus all your accessories.\n\n' +
								'**5x15 Units:** Ideal for longer touring kayaks or storing multiple boats. Great for families with several kayaks.\n\n' +
								'**5x20 Units:** The ultimate paddle sports storage solution. Store multiple kayaks, canoes, and create a dedicated gear organization system.',
						],
					},
				},
			],
		},
		{
			title: 'Average Storage Costs for Kayak Storage (2025)',
			modules: [
				{
					type: 'sizeChart',
					props: {
						title: 'Average Storage Costs for Kayak Storage (2025)',
						headers: [
							{
								label: 'Unit Size',
								key: 'Unit Size',
							},
							{
								label: 'Monthly Cost Range',
								key: 'Monthly Cost Range',
							},
							{
								label: 'Best For',
								key: 'Best For',
							},
						],
						rows: [
							{
								'Unit Size': '5x5',
								'Monthly Cost Range': '$45-$75',
								'Best For': 'Single whitewater kayak',
							},
							{
								'Unit Size': '5x10',
								'Monthly Cost Range': '$65-$110',
								'Best For': 'Most recreational kayaks',
							},
							{
								'Unit Size': '5x15',
								'Monthly Cost Range': '$85-$140',
								'Best For': 'Touring kayaks + gear',
							},
							{
								'Unit Size': '5x20',
								'Monthly Cost Range': '$110-$180',
								'Best For': 'Multiple kayaks/family storage',
							},
						],
					},
				},
				{
					type: 'content',
					props: {
						contentType: 'row-type',
						ShowBackgroundImage: false,
						content: [kayakStorageCost + '\n\n' + kayakStorageTips],
					},
				},
			],
		},

		{
			title: 'Reviews Section',
			background: 'dark',
			modules: [
				{
					type: 'reviews',
					props: {
						reviews,
						title: '4 Reasons to Choose SpareFoot for Kayak and Canoe Storage',
					},
				},
			],
		},
		{
			title: 'FAQ Section',
			modules: [
				{
					type: 'faqs',
					props: {
						header: 'Kayak Storage FAQs',
						faqs: kayakFaqs,
					},
				},
			],
		},
		{
			title: 'Inline CTA',
			noPadding: true,
			modules: [
				{
					type: 'inlineCTA',
					props: {
						title: 'Find the Best Kayak Storage For You',
						theme: 'dark',
					},
				},
			],
		},
		{
			title: 'Related Content',
			background: 'gray',
			modules: [
				{
					type: 'featuredContent',
					props: {
						slug: 'kayak-related-content',
						contentCards: [
							{
								content:
									'How to Winterize a Boat: Key Steps For Protecting Your Boat',
								date: 'September 3, 2024',
								image: Featured1,
								link: 'https://sparefoot.com/blog/how-to-winterize-a-boat',
								title: 'General Storage Advice',
							},
							{
								content:
									'How to Find The Right Storage Facility For Your Car, RV, or Boat',
								date: 'October 24, 2025',
								image: Featured2,
								link: 'https://sparefoot.com/blog/how-to-find-the-right-storage-facility-for-your-car-rv-or-boat',
								title: 'General Storage Advice',
							},
							{
								content:
									'Boat Storage Tips: How to Store Your Boat Safely and Efficiently',
								date: 'September 24, 2025',
								image: Featured3,
								link: 'https://sparefoot.com/blog/storing-a-boat',
								title: 'How to Store It',
							},
						],
					},
				},
			],
		},
		{
			title: 'Link to Other PSPs',
			background: 'yellow',
			modules: [
				{
					type: 'links',
					props: {
						title: 'More Ways to Store',
						cards: [
							'Vehicle',
							'Boat',
							'JetSki',
							'Bicycle',
							'RV',
							'24Hour',
							'DriveUp',
							'ClimateControl',
						],
					},
				},
			],
		},
		{
			title: 'nearby units',
			modules: [
				{
					type: 'storageNearYou',
				},
			],
		},
	],
};
